import axios from 'axios';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AddButton, ReplaceButton } from '@src/components/buttons';
import AddNewCampaignForm from '@src/components/forms/addWebCampaign.form';
import { useAuth } from '@src/contexts/authContext';
import { convertToJPG } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}/admin`;

export default function UpdateWebCampaign() {
  const { auth } = useAuth();
  const { bannerId } = useParams();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState([]);

  const [input, setInput] = useState({
    file: [],
    _id: [],
    campaign_link: [''],
  });
  const [updates, setUpdates] = useState({
    campaign_title: '',
    campaign_link: [],
    files: [],
    file_id: [],
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    campaign_status: ['ongoing', 'coming_soon', 'pause'],
    sorts: '',
  });

  async function updateWebBanner() {
    const uploadFormData = new FormData();
    const filesArray = input.file || [];

    await Promise.all(
      filesArray.map(async (file, index) => {
        let processedFile = file.file ?? file;

        if (processedFile instanceof File) {
          if (processedFile.type === 'image/png') {
            processedFile = await convertToJPG(processedFile, 2);
          }
        } else if (typeof processedFile === 'string') {
          const response = await fetch(processedFile);
          const blob = await response.blob();

          let cleanName =
            processedFile
              .split('/')
              .pop()
              ?.replace(/(resized-\d+-)+/g, '') || 'image.jpg';

          if (cleanName.length > 100) {
            const ext = cleanName.split('.').pop() || 'jpg';
            cleanName = `banner-${Date.now()}.${ext}`;
          }

          processedFile = new File([blob], cleanName, { type: blob.type });
        }

        uploadFormData.append('files', processedFile);
        if (file._id) uploadFormData.append(`file_id[${index}]`, file._id);
        if (file.campaign_link)
          uploadFormData.append(`campaign_link[${index}]`, file.campaign_link);
      }),
    );

    uploadFormData.append('campaign_title', input.campaign_title);
    uploadFormData.append('start_date', input.start_dates);
    uploadFormData.append('end_date', input.end_dates);
    uploadFormData.append('start_time', input.start_times);
    uploadFormData.append('end_time', input.end_times);
    uploadFormData.append('campaign_status', input.campaign_status);
    uploadFormData.append('sorts', input.sorts);

    axios
      .put(`${url}/web-banners/${bannerId}`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        setInput(response.data);
        toast.success('Banner updated successfully', {
          position: 'top-center',
          autoClose: '1000',
        });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        navigate('/announcement-marketing');
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message || 'Failed to update banner.',
          {
            position: 'top-center',
            autoClose: 3000,
          },
        );
        console.log(error);
      });
  }

  function updateItemBanner(index) {
    const fileObj = input?.file?.[index];
    const actualFile = fileObj?.file || fileObj?.originFileObj;

    if (!(actualFile instanceof File)) {
      console.warn('No valid file found to upload');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('first_image', actualFile);
    uploadFormData.append('banner_id', fileObj.banner_id || input?.banner_id);
    uploadFormData.append('campaign_link', fileObj.campaign_link || '');

    axios
      .post(`${url}/file-web-banner`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        const fileIds = response.data?.fileIds;
        if (Array.isArray(fileIds) && fileIds.length > 0) {
          setInput((prev) => {
            const updated = { ...prev };
            updated.file = [...(prev.file || [])];
            updated.file[index] = {
              ...(updated.file[index] || {}),
              _id: fileIds[0],
              file: undefined,
            };
            return updated;
          });
          console.log('File uploaded:', fileIds[0]);
        } else {
          console.warn('fileIds array is missing or empty:', fileIds);
        }
      })
      .catch((error) => {
        console.error('Error uploading file:', error);
      });
  }

  const handleItemFileChange = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      setPreviewUrl((prev) => {
        const updated = [...prev];
        updated[index] = reader.result;
        return updated;
      });

      const newFiles = [...(input.file || [])];
      newFiles[index] = {
        ...newFiles[index],
        file,
      };

      const updatedInput = {
        ...input,
        file: newFiles,
      };

      setInput(updatedInput);
      // updateItemBanner(input, updatedInput);
    };

    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < (input.file?.length || 0); i++) {
      const fileObj = input.file[i];
      if (fileObj && fileObj.file && !fileObj._id) {
        updateItemBanner(input, i);
      }
    }
    updateWebBanner();
    // handleOpenModal();
  };

  const fetchBannerData = async () => {
    try {
      const response = await axios.get(`${url}/web-banners/${bannerId}`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      });
      setInput(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleDeleteItemBanner = async (id) => {
    try {
      await axios.delete(`${url}/file-web-banner/${id}`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      });

      setInput((prev) => {
        if (!Array.isArray(prev.file)) return prev;
        return {
          ...prev,
          file: prev.file.filter((item) => item._id !== id),
        };
      });

      setPreviewUrl((prev) => {
        if (!Array.isArray(prev)) return [];
        return prev.filter((_, idx) => input.file[idx]?._id !== id);
      });

      setSelectedFile((prev) => {
        if (!Array.isArray(prev)) return [];
        return prev.filter((_, idx) => input.file[idx]?._id !== id);
      });

      toast.success('Item deleted successfully', {
        position: 'top-center',
        autoClose: '1000',
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete item', {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    fetchBannerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, bannerId]);

  function handleFileChange(e, index) {
    const file = e.target.files[0];
    if (!file) return;

    setInput((prev) => {
      const updated = { ...prev };
      updated.file = [...(prev.file || [])];

      if (updated.file[index]?._id || updated.file[index]?.file) {
        updated.file[index] = {
          ...(updated.file[index] || {}),
          file,
        };
      } else {
        updated.file.push({ file });
      }

      return updated;
    });

    setPreviewUrl((prev) => {
      const updatedPreview = [...(prev || [])];
      updatedPreview[index] = URL.createObjectURL(file);
      return updatedPreview;
    });
  }

  const handleLinkChange = (index, value) => {
    setInput((prevInput) => {
      const updatedFileArray = [...prevInput.file];
      if (updatedFileArray[index]) {
        updatedFileArray[index] = {
          ...updatedFileArray[index],
          campaign_link: value,
        };
      }
      return { ...prevInput, file: updatedFileArray };
    });
  };

  const handleBack = () => {
    navigate('/announcement-marketing');
  };
  return (
    <div className='py-8 px-8'>
      <Box sx={{ width: '100%' }}>
        <div className='flex w-[96%] justify-between'>
          <Button
            sx={{
              textTransform: 'none',
              paddingLeft: 0,
            }}
            className='py-6 px-6 font-bold text-purple-500'
            variant='text'
            // eslint-disable-next-line react/jsx-no-bind
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            sx={{
              textTransform: 'none',
              margin: 1,
              height: '50px',
              width: '150px',
            }}
            className='rounded-md border border-cyan-600 px-6 font-bold text-cyan-600'
            variant='outlined'
            // eslint-disable-next-line react/jsx-no-bind
            onClick={handleSave}
            onNavigate={() => {
              navigate('/announcement-marketing');
            }}
          >
            Save
          </Button>
        </div>
      </Box>
      <br />
      <Box
        className='w-[96%] rounded-xl py-4 px-4'
        sx={{ width: '100%', height: '151px', backgroundColor: 'white' }}
      >
        <AddNewCampaignForm
          input={input}
          setInput={setInput}
          updates={updates}
          setUpdates={setUpdates}
        />
      </Box>

      <div className='py-4 px-4'>
        <div className='py-2 text-sm font-bold text-gray-500'>
          Horizontal Desktop Screen Admin
        </div>
        <div className='text-xs text-gray-400'>(Best size 938px by 198px)</div>
      </div>
      <br />
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: 'gray',
        }}
        className='rounded-xl py-4 px-4'
      >
        <div className='flex' style={{ width: 'calc(695px * 5)' }}>
          {[0, 1, 2, 3, 4].map((index) => {
            const fileData = input?.file?.[index] || {};

            return (
              <Box
                key={index}
                sx={{
                  width: '655px',
                  height: 'auto',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    margin: 2,
                    width: '635px',
                    height: '135px',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  className='flex justify-center bg-gray-300'
                >
                  <div>
                    <input
                      type='file'
                      accept='image/*'
                      onChange={(e) => {
                        if (!fileData?.file) {
                          handleItemFileChange(e, index);
                        } else {
                          handleFileChange(e, index);
                        }
                      }}
                      name='files'
                      multiple
                      style={{ display: 'none' }}
                      id={`upload-file-input-${index}`}
                    />

                    {((Array.isArray(previewUrl) && previewUrl[index]) ||
                      fileData?.file) && (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={
                            Array.isArray(previewUrl) && previewUrl[index]
                              ? previewUrl[index]
                              : fileData?.file || ''
                          }
                          alt=''
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                          }}
                        />
                      </div>
                    )}

                    <label htmlFor={`upload-file-input-${index}`}>
                      {fileData?._id || fileData?.file ? (
                        <ReplaceButton />
                      ) : (
                        <AddButton />
                      )}
                      <br />
                    </label>

                    <Button
                      sx={{
                        textTransform: 'none',
                        position: 'absolute',
                        top: '50%',
                        left: '70%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                      }}
                      variant='contained'
                      component='span'
                      className='bg-red-500 text-xs font-bold'
                      onClick={() =>
                        handleDeleteItemBanner(fileData?._id || '')
                      }
                    >
                      Remove
                    </Button>
                    <br />
                  </div>
                </Box>

                <div className='col-span-4 px-4'>
                  <div className='mt-2 py-4'>
                    <input
                      placeholder='Enter link'
                      style={{ width: '635px' }}
                      value={fileData.campaign_link || ''}
                      onChange={(e) => handleLinkChange(index, e.target.value)}
                      className='font-poppins block w-full rounded-md border-0 bg-white px-2 py-3.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
                    />
                  </div>
                </div>
              </Box>
            );
          })}
        </div>

        <div className='px-6 text-xs text-gray-300'>*image is required</div>
      </Box>

      <br />
      <br />
      <br />
    </div>
  );
}
